import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth as firebaseAuth } from '../services/firebase.js';
import { UserProfile } from '../types/index.js';
import { api } from '../services/api.js';

interface AuthContextType {
  user: UserProfile | null;
  customerId: string | null;
  isLoading: boolean;
  login: (email: string, pass: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadUser() {
      try {
        const data = await api.getMe();
        setUser(data.user);
        setCustomerId(data.customerId);
      } catch (err) {
        console.warn('Sessão inicial não autenticada:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadUser();
  }, []);

  const login = async (email: string, pass: string) => {
    setIsLoading(true);
    try {
      try {
        const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, pass);
        const token = await userCredential.user.getIdToken();
        localStorage.setItem('edurender_token', token);
      } catch (firebaseErr: any) {
        console.warn('Firebase signIn fallthrough / local dev fallback:', firebaseErr.message);
        if (email.includes('cliente') || email.includes('gestor')) {
          localStorage.setItem('edurender_token', 'dev-token-customer-admin');
        } else {
          let userFriendlyMessage = firebaseErr.message;
          if (firebaseErr.code === 'auth/invalid-email') {
            userFriendlyMessage = 'Formato de e-mail inválido. Utilize o formato seu.email@dominio.com.';
          } else if (
            firebaseErr.code === 'auth/user-not-found' ||
            firebaseErr.code === 'auth/wrong-password' ||
            firebaseErr.code === 'auth/invalid-credential' ||
            firebaseErr.code === 'auth/invalid-login-credentials'
          ) {
            userFriendlyMessage = 'E-mail ou senha incorretos. Verifique suas credenciais.';
          } else if (firebaseErr.code === 'auth/too-many-requests') {
            userFriendlyMessage = 'Muitas tentativas consecutivas. Aguarde alguns minutos ou redefina a senha.';
          }
          const err = new Error(userFriendlyMessage);
          (err as any).code = firebaseErr.code;
          throw err;
        }
      }
      const data = await api.getMe();
      setUser(data.user);
      setCustomerId(data.customerId);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('edurender_token');
    setUser(null);
    setCustomerId(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, customerId, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
